import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import weekday from "dayjs/plugin/weekday";
import type {
  ICountDownTimeAtTime,
  ICountDownTimeBase,
  ITimerCountDown,
  ITimerCountDownRepeat,
  Timer,
} from "../node/type";
import { isNotFalsy } from "./value";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);

export const setRAFInterval = (callback: () => void, duration: number) => {
  let _startTime: number | null = null;
  let _stopped = false;

  const action = (timestamp: number) => {
    if (!_startTime) {
      _startTime = timestamp;
    }

    const elapsed = timestamp - _startTime;
    if (elapsed >= duration) {
      callback();
      _startTime = null;
    }

    if (!_stopped) {
      requestAnimationFrame(action);
    }
  };

  requestAnimationFrame(action);

  return () => {
    _stopped = true;
  };
};

// TODO: 포맷에 맞게 숫자 보여주기, 현재는 dd일 hh:mm:ss 고정
export const getFormattedTimeLeft = (msLeft: number, _format: string) => {
  if (msLeft <= 0) {
    return `00일 00:00:00`;
  }

  const secLeft = Math.floor(msLeft / 1000);
  const days = Math.floor(secLeft / 86400)
    .toString()
    .padStart(2, "0");
  const hours = Math.floor((secLeft % 86400) / 3600)
    .toString()
    .padStart(2, "0");
  const minutes = Math.floor((secLeft % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (secLeft % 60).toString().padStart(2, "0");

  return `${days}일 ${hours}:${minutes}:${seconds}`;
};

export const getCountdownType = (countdown: Timer) => {
  return countdown.startAt === "DYNAMIC" ? "DYNAMIC" : "STATIC";
};

export const getCountdownTimeLeft = (countdown: Timer) => {
  const countdownType = getCountdownType(countdown);

  // 고정형
  if (countdownType === "STATIC") {
    // 반복 없음
    if (countdown.type === "COUNTDOWN") {
      return dayjs(countdown.endAt).diff(dayjs());
    }

    // 요일마다
    if (countdown.type === "COUNTDOWN_REPEAT") {
      const now = dayjs();
      const adjustedNow = now.subtract(
        now.utcOffset() + (countdown.utcOffset ?? -540),
        "m"
      );
      const adjustedDay = adjustedNow.day();

      if (!countdown.repeat.includes(adjustedDay)) {
        return 0;
      }

      const start = adjustedNow
        .set("h", countdown.startAt.h)
        .set("m", countdown.startAt.m)
        .set("s", countdown.startAt.s);

      if (start.isAfter(adjustedNow)) {
        return 0;
      }

      const end = adjustedNow
        .set("h", countdown.endAt.h)
        .set("m", countdown.endAt.m)
        .set("s", countdown.endAt.s);

      return end.diff(adjustedNow);
    }

    throw new Error("Not implemented");
  } else {
    throw new Error("Not implemented");
  }
};

export const getLargestTimeUnit = (timeUnits: string[]) => {
  if (timeUnits.includes("d")) {
    return "d";
  }
  if (timeUnits.includes("h")) {
    return "h";
  }
  if (timeUnits.includes("m")) {
    return "m";
  }
  if (timeUnits.includes("s")) {
    return "s";
  }
  return "s";
};

export const getTimeUnitsFromContent = (content: string) => {
  const doubleBracesTimeStringRegex =
    /{{(?:\s|&nbsp;)*(?:time\.)(.*?)(?:\s|&nbsp;)*}}/g;
  const matchedUnits = [...content.matchAll(doubleBracesTimeStringRegex)]
    .map((m) => m[1])
    .filter(isNotFalsy);
  return matchedUnits;
};

export const getTimeLeftByUnits = (msLeft: number, units: string[]) => {
  const timeLefts = {
    dd: "00",
    d: "0",
    h: "00",
    m: "00",
    s: "00",
  };
  if (msLeft <= 0) {
    return timeLefts;
  }

  const timeUnits = {
    dd: false,
    d: false,
    h: false,
    m: false,
    s: false,
  };

  for (const unit of units) {
    if (
      unit === "dd" ||
      unit === "d" ||
      unit === "h" ||
      unit === "m" ||
      unit === "s"
    ) {
      timeUnits[unit] = true;
    }
  }

  if (timeUnits.dd || timeUnits.d) {
    const d = Math.floor(msLeft / 86400000);
    if (timeUnits.dd) {
      timeLefts.dd = d.toString().padStart(2, "0");
    }
    if (timeUnits.d) {
      timeLefts.d = d.toString().padStart(1, "0");
    }

    msLeft -= d * 86400000;
  }
  if (timeUnits.h) {
    const h = Math.floor(msLeft / 3600000);
    timeLefts.h = h.toString().padStart(2, "0");
    msLeft -= h * 3600000;
  }
  if (timeUnits.m) {
    const m = Math.floor(msLeft / 60000);
    timeLefts.m = m.toString().padStart(2, "0");
    msLeft -= m * 60000;
  }
  if (timeUnits.s) {
    const s = Math.floor(msLeft / 1000);
    timeLefts.s = s.toString().padStart(2, "0");
  }

  return timeLefts;
};

export const formatsToContent = (format: string) => {
  switch (format) {
    case "dd일":
      return "<p>{{time.dd}}일</p>";
    case "dd일 hh:mm":
      return "<p>{{time.dd}}일 {{time.h}}:{{time.m}}</p>";
    case "dd일 hh:mm:ss":
      return "<p>{{time.dd}}일 {{time.h}}:{{time.m}}:{{time.s}}</p>";
    case "hh:mm":
      return "<p>{{time.h}}:{{time.m}}</p>";
    case "hh:mm:ss":
      return "<p>{{time.h}}:{{time.m}}:{{time.s}}</p>";
    default:
      return format;
  }
};

export const hasRepeat = (timer: Timer): timer is ITimerCountDownRepeat => {
  return timer.type === "COUNTDOWN_REPEAT";
};

export const createCountdownRepeat = (
  timer: Partial<ITimerCountDownRepeat>
): ITimerCountDownRepeat => {
  return {
    type: "COUNTDOWN_REPEAT",
    repeat: timer.repeat ?? [],
    startAt: timer.startAt ?? { type: "AT_TIME", d: 0, h: 0, m: 0, s: 0 },
    endAt: timer.endAt ?? { type: "AT_TIME", d: 0, h: 23, m: 59, s: 59 },
    format: timer.format ?? "dd일 hh:mm:ss",
    endAction: timer.endAction ?? { type: "NONE" },
    utcOffset: timer.utcOffset ?? new Date().getTimezoneOffset(),
    ui: timer.ui ?? { showEndMessage: false },
  };
};

export const createCountdown = (
  timer: Partial<ITimerCountDown>
): ITimerCountDown => {
  return {
    type: "COUNTDOWN",
    repeat: null,
    startAt: null,
    endAt: timer.endAt ?? dayjs().add(1, "day").toISOString(),
    format: timer.format ?? "dd일 hh:mm:ss",
    endAction: timer.endAction ?? { type: "NONE" },
    utcOffset: timer.utcOffset ?? new Date().getTimezoneOffset(),
    ui: timer.ui ?? { showEndMessage: false },
  };
};

export const createCountdownAtTime = (
  timer: Partial<ICountDownTimeAtTime>
): ICountDownTimeAtTime => {
  return {
    type: "AT_TIME",
    d: timer.d ?? 0,
    h: timer.h ?? 0,
    m: timer.m ?? 0,
    s: timer.s ?? 0,
  };
};

export const toggleRepeatDay = (
  timer: ITimerCountDownRepeat,
  day: number
): ITimerCountDownRepeat => {
  return {
    ...timer,
    repeat: timer.repeat?.includes(day)
      ? timer.repeat?.filter((d) => d !== day)
      : [...(timer.repeat ?? []), day],
  };
};

export const countdownTimeToString = (countdownTime: ICountDownTimeBase) => {
  return `${countdownTime.h}:${countdownTime.m}:${countdownTime.s}`;
};

export function splitTime(time: string) {
  const [hour, minute] = time.split(":");
  if (hour === undefined || minute === undefined) {
    throw new Error("Invalid time");
  }

  return { hour: Number(hour), minute: Number(minute) };
}

export function isTimeInRange(
  startTime: string,
  endTime: string,
  currentTime: string
) {
  const { hour: startTimeHour, minute: startTimeMinute } = splitTime(startTime);
  const { hour: endTimeHour, minute: endTimeMinute } = splitTime(endTime);
  const { hour: currentTimeHour, minute: currentTimeMinute } =
    splitTime(currentTime);

  // 시간범위 밖인지 확인 (ex: 10:20 ~ 12:50, 현재 09:00)
  if (currentTimeHour < startTimeHour || currentTimeHour > endTimeHour) {
    return false;
  }

  // 시간범위 내인지 확인 (ex: 10:20 ~ 12:50, 현재 11:00)
  if (currentTimeHour > startTimeHour && currentTimeHour < endTimeHour) {
    return true;
  }

  // 시작시간과 종료시간이 같은 경우 분 확인 (ex: 10:10 ~ 10:50, 현재 10:15)
  // 종료된 방송의 상품이 보이면 안되기 때문에 안전하게 종료시간 1분전에는 보이지 않도록 함
  if (currentTimeHour === startTimeHour && currentTimeHour === endTimeHour) {
    return (
      currentTimeMinute >= startTimeMinute &&
      currentTimeMinute < endTimeMinute - 1
    );
  }

  // 시작시간과 같은 경우 분 확인 (ex: 10:10 ~ 11:50, 현재 10:15)
  if (currentTimeHour === startTimeHour) {
    return currentTimeMinute >= startTimeMinute;
  }

  // 종료시간과 같은 경우 분 확인 (ex: 10:10 ~ 11:50, 현재 11:30)
  // 종료된 방송의 상품이 보이면 안되기 때문에 안전하게 종료시간 1분전에는 보이지 않도록 함
  if (currentTimeHour === endTimeHour) {
    return currentTimeMinute < endTimeMinute - 1;
  }

  return false;
}
